import type { ColDef } from "ag-grid-community";
import type { IRowNode } from "ag-grid-community";
import { useCallback, useMemo, useRef } from "react";

type ColumnFilter = { field: string; value: string };

function matchesNumericFilter(
  filterValue: string,
  cellValue: unknown,
): boolean {
  // Number("") is 0, which would make an empty cell match "<100".
  if (cellValue === null || cellValue === undefined || cellValue === "") {
    return false;
  }

  const cellStr = String(cellValue);

  const numericOp = filterValue.match(/^(>=|<=|>|<)(.+)$/);
  if (!numericOp) return false;

  const threshold = Number(numericOp[2]);
  if (Number.isNaN(threshold)) return false;

  const cellNum = Number(cellStr);
  if (Number.isNaN(cellNum)) return false;

  switch (numericOp[1]) {
    case ">":
      return cellNum > threshold;
    case "<":
      return cellNum < threshold;
    case ">=":
      return cellNum >= threshold;
    case "<=":
      return cellNum <= threshold;
  }
  return false;
}

function matchesWildcard(pattern: string, text: string): boolean {
  return new RegExp(
    `^${pattern
      .split("*")
      .map((s) => s.replace(/[.+?^${}()|[\]\\]/g, "\\$&"))
      .join(".*")}$`,
    "i",
  ).test(text);
}

function matchesRegex(pattern: string, flags: string, text: string): boolean {
  try {
    return new RegExp(pattern, flags).test(text);
  } catch {
    return false;
  }
}

function matchesColumnFilter(filterValue: string, cellValue: unknown): boolean {
  const cellStr = String(cellValue ?? "");

  // Handle numeric filters (e.g., ">=100").
  const numericOp = filterValue.match(/^(>=|<=|>|<)(.+)$/);
  if (numericOp) {
    return matchesNumericFilter(filterValue, cellValue);
  }

  // Handle regex filters (e.g., "/^dev.*sandbox$/i").
  const regexOp = filterValue.match(/^\/(.+)\/([gimsuy]*)$/);
  if (regexOp) {
    return matchesRegex(regexOp[1], regexOp[2], cellStr);
  }

  // Handle wildcard filters (e.g., "handle:*eric*").
  const wildcardOp = filterValue.includes("*");
  if (wildcardOp) {
    return matchesWildcard(filterValue, cellStr);
  }

  // Default to exact match with smart case-insensitive matching.
  return (
    cellStr.localeCompare(filterValue, undefined, { sensitivity: "base" }) === 0
  );
}

function parseSearchText(text: string): {
  columnFilters: ColumnFilter[];
  quickFilter: string;
} {
  const columnFilters: ColumnFilter[] = [];
  const quickFilterParts: string[] = [];

  for (const token of text.trim().split(/\s+/)) {
    if (token.includes(":")) {
      const [field, value] = token.split(":", 2);
      columnFilters.push({ field, value });
    } else {
      quickFilterParts.push(token.trim());
    }
  }

  return { columnFilters, quickFilter: quickFilterParts.join(" ") };
}

/**
 * Slugify a column name by removing whitespace and non-alphanumeric characters
 * and converting to lowercase. This is useful for creating a unique identifier
 * for a column that can be used for filtering.
 *
 * Example: "Est. Monthly Cost" -> "estmonthlycost".
 */
const slugifyColumnName = (value: string): string =>
  value
    .trim()
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();

export const useExternalFilter = <T>(
  searchText: string,
  colDefs: ColDef<T>[],
  selectable = false,
) => {
  const colDefsBySlug = useMemo(
    () =>
      colDefs.reduce(
        (acc, col) => {
          const fieldSlug = slugifyColumnName(col.field ?? "");
          if (fieldSlug) {
            acc[fieldSlug] = col;
          }

          const colIdSlug = slugifyColumnName(col.colId ?? "");
          if (colIdSlug) {
            acc[colIdSlug] = col;
          }

          const headerNameSlug = slugifyColumnName(col.headerName ?? "");
          if (headerNameSlug) {
            acc[headerNameSlug] = col;
          }
          return acc;
        },
        {} as Record<string, ColDef<T>>,
      ),
    [colDefs],
  );

  // Fancy filtering: we'll parse the search text into column filters (e.g.,
  // "handle:sbx-eric") and a quick filter (e.g., "other terms").
  const { columnFilters, quickFilter } = useMemo(
    () => parseSearchText(searchText),
    [searchText],
  );

  // ag-grid caches doesExternalFilterPass at init and won't swap in a new
  // function reference on re-render, so a direct closure over columnFilters
  // would go stale. The ref stays constant while .current is always fresh.
  const columnFiltersRef = useRef(columnFilters);
  columnFiltersRef.current = columnFilters;

  // Tell ag-grid that column filters are active.
  const isExternalFilterPresent = () => columnFiltersRef.current.length > 0;

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<T>) => {
      // Ignore empty rows.
      if (!node.data) return true;

      // Check if the row matches all of the column filters.
      return columnFiltersRef.current.every(({ field, value }) => {
        /* Selection isn't a column, and ag-grid doesn't re-filter when it
         * changes, so this reads as a snapshot taken when the term was typed.
         */
        if (selectable && slugifyColumnName(field) === "selected") {
          return matchesColumnFilter(value, node.isSelected());
        }

        // Find the column definition by its slugified field or header name.
        const colDef = colDefsBySlug[slugifyColumnName(field)];
        const columnKey = colDef?.colId || colDef?.field;
        if (!colDef || !columnKey) return false;

        return matchesColumnFilter(value, node.getDataValue(columnKey));
      });
    },
    [colDefs, columnFiltersRef, selectable],
  );

  return {
    columnFilters,
    quickFilter,
    isExternalFilterPresent,
    doesExternalFilterPass,
  };
};
